import { useEffect, useRef, useState } from "react";
import { SettingsContext } from "./SettingsContext";
import type { User } from "@rowan/prisma";
import { useUser } from "../../hooks/useUser";
import { trpc } from "../../../utils/trpc";
import { shallowEqual, useDebounce } from "../../utils";

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [ereaderSettings, setEreaderSettings] =
    useState<User["ereaderSettings"]>();
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const { user } = useUser();

  const updateSettings = trpc.updateUserSettings.useMutation();

  const debouncedUpdateSettings = useDebounce(
    (settings: User["ereaderSettings"]) => {
      if (
        (settings &&
          user?.ereaderSettings &&
          shallowEqual(settings, user?.ereaderSettings)) ||
        !settings
      )
        return;
      updateSettings.mutate({
        settings,
      });
    },
    500,
  );

  const didInitialise = useRef(false);

  useEffect(() => {
    if (!user || didInitialise.current) return;
    setEreaderSettings(user.ereaderSettings);
    didInitialise.current = true;
  }, [user]);

  useEffect(() => {
    if (!ereaderSettings) return;
    debouncedUpdateSettings(ereaderSettings);
  }, [ereaderSettings, debouncedUpdateSettings]);

  return (
    <SettingsContext.Provider
      value={{
        ereaderSettings,
        setEreaderSettings,
        settingsOpen,
        setSettingsOpen,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
